import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import persianDate from 'persian-date';

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await api.post('/api/auth/refresh-token', { refreshToken });
      localStorage.setItem('authToken', res.data.accessToken);
      return res.data.accessToken;
    } catch (err) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login_page');
      return null;
    }
  };

  const fetchTransactions = async () => {
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const res = await api.get('/api/transactions/all', {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setTransactions(res.data.transactions);
    } catch (err) {
      if (err.response?.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          const res = await api.get('/api/transactions/all', {
            headers: { Authorization: `Bearer ${newToken}` }
          });
          setTransactions(res.data.transactions);
        } else {
          setError('جلسه شما منقضی شده است. لطفاً دوباره وارد شوید.');
          setTimeout(() => navigate('/login_page'), 2000);
        }
      } else {
        setError(err.response?.data?.message || 'خطا در دریافت صورت‌حساب');
      }
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login_page');
      return;
    }
    fetchTransactions();
  }, [token, navigate]);

  const getTypeText = (type) => {
    const types = {
      wallet_deposit: 'شارژ کیف پول',
      wallet_withdraw: 'برداشت از کیف پول',
      gold_deposit: 'شارژ کیف طلا',
      reward_referral: 'هدیه معرف کاربر',
      buy: 'خرید طلا',
      sell: 'فروش طلا',
      gold_request: 'درخواست آبشده'
    };
    return types[type] || type;
  };

  const getStatusText = (status) => {
    const statuses = {
      approved: 'تأیید شده',
      ready: 'آماده تحویل',
      delivered: 'تحویل داده شده',
      rejected: 'رد شده',
      deposited: 'واریز شده'
    };
    return statuses[status] || status;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
      case 'deposited':
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'ready':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (date) => {
    try {
      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? '—' : new persianDate(parsed).format('YYYY/MM/DD HH:mm:ss');
    } catch {
      return '—';
    }
  };

  // محاسبه مانده‌ها (از قدیمی به جدید)
  const calculateRunningBalances = (transactions) => {
    let runningRial = 0;
    let runningGold = 0;
    return transactions.map(tx => {
      const creditRial = parseFloat(tx.credit_rial) || 0;
      const debitRial = parseFloat(tx.debit_rial) || 0;
      const creditGold = parseFloat(tx.credit_gold) || 0;
      const debitGold = parseFloat(tx.debit_gold) || 0;

      runningRial += creditRial - debitRial;
      runningGold += creditGold - debitGold;

      return { ...tx, running_rial_balance: runningRial, running_gold_balance: runningGold };
    });
  };

  // 1. مرتب‌سازی صعودی (قدیمی → جدید) برای محاسبه مانده
  const sortedAsc = [...transactions].sort((a, b) => 
    new Date(a.created_at) - new Date(b.created_at)
  );

  // 2. محاسبه مانده‌ها
  const withBalances = calculateRunningBalances(sortedAsc);

  // 3. برعکس کردن برای نمایش (جدید → قدیمی)
  const displayedTransactions = withBalances.reverse();

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
        <div className="max-w-7xl mx-auto">

          {/* پیام خطا */}
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ y: -100, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -100, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 120 }}
                >
                  <div className="bg-red-500/90 backdrop-blur-lg text-white px-6 py-4 rounded-2xl shadow-2xl border border-red-300 flex items-center gap-3">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" />
                    </svg>
                    <span className="font-medium">{error}</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* عنوان */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-center bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-10"
          >
            صورت‌حساب کلی
          </motion.h2>

          {/* جدول دسکتاپ */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="hidden md:block bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden"
          >
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">لیست تراکنش‌ها</h3>
            </div>
            {displayedTransactions.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-500">هیچ تراکنشی یافت نشد</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-right">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-3 font-semibold">شماره سند</th>
                      <th className="px-4 py-3 font-semibold">تاریخ</th>
                      <th className="px-4 py-3 font-semibold">نوع</th>
                      <th className="px-4 py-3 font-semibold">وضعیت</th>
                      <th className="px-4 py-3 font-semibold">شرح</th>
                      <th className="px-4 py-3 font-semibold">قیمت هر گرم</th>
                      <th className="px-4 py-3 font-semibold">بدهکار وزنی</th>
                      <th className="px-4 py-3 font-semibold">بستانکار وزنی</th>
                      <th className="px-4 py-3 font-semibold">مانده وزنی</th>
                      <th className="px-4 py-3 font-semibold">بدهکار ریالی</th>
                      <th className="px-4 py-3 font-semibold">بستانکار ریالی</th>
                      <th className="px-4 py-3 font-semibold">مانده ریالی</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {displayedTransactions.map((tx, i) => (
                      <motion.tr
                        key={tx.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="hover:bg-indigo-50/50 transition-colors"
                      >
                        <td className="px-4 py-3 font-mono text-indigo-700">#{tx.id}</td>
                        <td className="px-4 py-3 text-gray-600">{formatDate(tx.created_at)}</td>
                        <td className="px-4 py-3">{getTypeText(tx.type)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(tx.status)}`}>
                            {getStatusText(tx.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{tx.description || '—'}</td>
                        <td className="px-4 py-3 font-mono">{tx.price_per_gram ? parseFloat(tx.price_per_gram).toLocaleString('fa-IR') : '—'}</td>
                        <td className="px-4 py-3 font-mono text-red-600">{tx.debit_gold ? parseFloat(tx.debit_gold).toFixed(3) : '—'}</td>
                        <td className="px-4 py-3 font-mono text-green-600">{tx.credit_gold ? parseFloat(tx.credit_gold).toFixed(3) : '—'}</td>
                        <td className="px-4 py-3 font-mono font-bold text-indigo-700">{parseFloat(tx.running_gold_balance).toFixed(3)}</td>
                        <td className="px-4 py-3 font-mono text-red-600">{tx.debit_rial ? parseFloat(tx.debit_rial).toLocaleString('fa-IR') : '—'}</td>
                        <td className="px-4 py-3 font-mono text-green-600">{tx.credit_rial ? parseFloat(tx.credit_rial).toLocaleString('fa-IR') : '—'}</td>
                        <td className="px-4 py-3 font-mono font-bold text-indigo-700">{parseFloat(tx.running_rial_balance).toLocaleString('fa-IR')}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>

          {/* کارت‌های موبایل */}
          {displayedTransactions.map((tx, i) => (
  <motion.div
    key={tx.id}
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: i * 0.1 }}
    className="bg-white rounded-3xl p-5 shadow-lg border border-gray-200"
  >
    {/* سرتیتر: شماره سند، نوع، تاریخ، وضعیت */}
    <div className="flex justify-between items-start mb-3">
      <div>
        <p className="font-bold text-indigo-700">#{tx.id}</p>
        <p className="text-sm text-gray-600">{getTypeText(tx.type)}</p>
      </div>
      <div>
        <p className="text-gray-500">تاریخ</p>
        <p className="text-gray-600">{formatDate(tx.created_at)}</p>
      </div>
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(tx.status)}`}>
        {getStatusText(tx.status)}
      </span>
    </div>

    {/* شرح */}
    {tx.description && <p className="text-xs text-gray-500 mb-3">{tx.description}</p>}

    {/* بدهکار/بستانکار + قیمت هر گرم (در یک grid) */}
    <div className="grid grid-cols-2 gap-3 text-xs">
      {/* بدهکار وزنی */}
      {tx.debit_gold > 0 && (
        <div>
          <p className="text-red-600">بدهکار وزنی</p>
          <p className="font-mono text-red-600">{parseFloat(tx.debit_gold).toFixed(3)} گرم</p>
        </div>
      )}

      {/* بستانکار وزنی */}
      {tx.credit_gold > 0 && (
        <div>
          <p className="text-green-600">بستانکار وزنی</p>
          <p className="font-mono text-green-600">{parseFloat(tx.credit_gold).toFixed(3)} گرم</p>
        </div>
      )}

      {/* بدهکار ریالی */}
      {tx.debit_rial > 0 && (
        <div>
          <p className="text-red-600">بدهکار ریالی</p>
          <p className="font-mono text-red-600">{parseFloat(tx.debit_rial).toLocaleString('fa-IR')} ریال</p>
        </div>
      )}

      {/* بستانکار ریالی */}
      {tx.credit_rial > 0 && (
        <div>
          <p className="text-green-600">بستانکار ریالی</p>
          <p className="font-mono text-green-600">{parseFloat(tx.credit_rial).toLocaleString('fa-IR')} ریال</p>
        </div>
      )}

      {/* قیمت هر گرم — فقط اگر وجود داشته باشد */}
      {tx.price_per_gram > 0 && (
  <div className="col-span-2">
    <p className="text-gray-600 font-medium">قیمت هر گرم</p>
    <p 
      className={`font-mono font-semibold ${
        tx.type === 'buy' 
          ? 'text-blue-600' 
          : tx.type === 'sell' 
            ? 'text-red-600' 
            : 'text-indigo-700'
      }`}
    >
      {parseFloat(tx.price_per_gram).toLocaleString('fa-IR')} ریال
    </p>
  </div>
)}
    </div>

    {/* مانده‌ها — همیشه نمایش داده میشن */}
    <div className="grid grid-cols-2 gap-3 text-xs mt-4 pt-3 border-t border-gray-200">
      <div>
        <p className="text-gray-500">مانده ریالی</p>
        <p className="font-bold text-indigo-700">{parseFloat(tx.running_rial_balance).toLocaleString('fa-IR')} ریال</p>
      </div>
      <div>
        <p className="text-gray-500">مانده وزنی</p>
        <p className="font-bold text-indigo-700">{parseFloat(tx.running_gold_balance).toFixed(3)} گرم</p>
      </div>
    </div>
  </motion.div>
))}
        </div>
      </div>
    </>
  );
}